import { getBeWsUrl } from "@/lib/api/be";
import { getStoredToken } from "@/lib/session/clientAuth";

export type WsGameType = "SEOTDA" | "HOLDEM";

export type WsStatus = "DISCONNECTED" | "CONNECTING" | "ONLINE" | "RECONNECTING";

export type WsHandlers = {
  onMessage: (msg: any) => void;
  onAuthOk?: () => void;
  onAuthFail?: (error: string) => void;
};

type StatusListener = (s: WsStatus) => void;

type ChannelState = {
  ws: WebSocket | null;
  status: WsStatus;
  authed: boolean;
  shouldReconnect: boolean;
  attempt: number;
  retryTimer: number | null;
  uiStatusTimer: number | null;
  handlers: Set<WsHandlers>;
  statusListeners: Set<StatusListener>;
  lastRttMs: number | null;
  rttListeners: Set<(ms: number | null) => void>;
  pingTimer: number | null;
  pendingPingTs: number | null;
  pendingPingSentAt: number;
};

function emptyChannel(): ChannelState {
  return {
    ws: null,
    status: "DISCONNECTED",
    authed: false,
    shouldReconnect: true,
    attempt: 0,
    retryTimer: null,
    uiStatusTimer: null,
    handlers: new Set(),
    statusListeners: new Set(),
    lastRttMs: null,
    rttListeners: new Set(),
    pingTimer: null,
    pendingPingTs: null,
    pendingPingSentAt: 0,
  };
}

const channels: Record<WsGameType, ChannelState> = {
  SEOTDA: emptyChannel(),
  HOLDEM: emptyChannel(),
};

/** RTT 배지 등: 채널 미지정 시 두 소켓 중 측정된 값의 최대 */
const mergedRttListeners = new Set<(ms: number | null) => void>();

function notifyMergedRtt() {
  if (mergedRttListeners.size === 0) return;
  const a = channels.SEOTDA.lastRttMs;
  const b = channels.HOLDEM.lastRttMs;
  const nums = [a, b].filter((x): x is number => x != null && Number.isFinite(x));
  const merged = nums.length ? Math.max(...nums) : null;
  mergedRttListeners.forEach((fn) => {
    try {
      fn(merged);
    } catch {
      // ignore
    }
  });
}

function setStatus(gt: WsGameType, s: WsStatus) {
  const ch = channels[gt];
  ch.status = s;
  ch.statusListeners.forEach((fn) => fn(s));
}

function notifyRtt(gt: WsGameType, ms: number | null) {
  const ch = channels[gt];
  ch.lastRttMs = ms;
  ch.rttListeners.forEach((fn) => {
    try {
      fn(ms);
    } catch {
      // ignore
    }
  });
  notifyMergedRtt();
}

function stopLatencyPing(gt: WsGameType) {
  const ch = channels[gt];
  if (ch.pingTimer != null && typeof window !== "undefined") {
    window.clearInterval(ch.pingTimer);
  }
  ch.pingTimer = null;
  ch.pendingPingTs = null;
  ch.pendingPingSentAt = 0;
  notifyRtt(gt, null);
}

function startLatencyPing(gt: WsGameType) {
  if (typeof window === "undefined") return;
  const ch = channels[gt];
  if (ch.pingTimer != null) return;

  ch.pingTimer = window.setInterval(() => {
    const ws = ch.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN || !ch.authed) return;

    if (ch.pendingPingTs !== null && ch.pendingPingSentAt > 0) {
      notifyRtt(gt, Math.max(0, Date.now() - ch.pendingPingSentAt));
    }

    const ts = Date.now();
    ch.pendingPingTs = ts;
    ch.pendingPingSentAt = ts;
    try {
      ws.send(JSON.stringify({ t: "CLIENT_PING", ts }));
    } catch {
      ch.pendingPingTs = null;
    }
  }, 1000);
}

function handleClientPongMsg(gt: WsGameType, msg: { ts?: number }) {
  const ch = channels[gt];
  const ts = Number((msg as any).ts);
  if (!Number.isFinite(ts) || ch.pendingPingTs === null) return;
  if (ts !== ch.pendingPingTs) return;
  ch.pendingPingTs = null;
  notifyRtt(gt, Math.max(0, Date.now() - ch.pendingPingSentAt));
}

function shutdownChannel(gt: WsGameType, opts?: { notifyDisconnected?: boolean }) {
  const ch = channels[gt];
  stopLatencyPing(gt);
  ch.shouldReconnect = false;
  ch.authed = false;
  if (ch.retryTimer) window.clearTimeout(ch.retryTimer);
  ch.retryTimer = null;
  if (ch.uiStatusTimer) window.clearTimeout(ch.uiStatusTimer);
  ch.uiStatusTimer = null;
  try {
    ch.ws?.close();
  } catch {
    // ignore
  }
  ch.ws = null;
  ch.attempt = 0;
  if (opts?.notifyDisconnected !== false) {
    setStatus(gt, "DISCONNECTED");
  }
}

/** 로그아웃·세션 삭제 시 섯다/홀덤 WS 모두 종료 */
export function wsDisconnectAll() {
  if (typeof window === "undefined") return;
  (["SEOTDA", "HOLDEM"] as const).forEach((gt) => shutdownChannel(gt));
}

/**
 * 구독자가 없을 때 소켓을 닫을지.
 * 로그인 상태(토큰 있음)에서는 탭 전환·리프레시로 0명이 잠깐 되어도 닫지 않아 세션 난립을 막는다.
 */
function shouldCloseSocketWhenNoHandlers(): boolean {
  return !getStoredToken();
}

function ensureConnect(gt: WsGameType) {
  if (typeof window === "undefined") return;

  const ch = channels[gt];
  if (ch.ws && ch.ws.readyState !== WebSocket.CLOSED) return;

  const token = getStoredToken();
  if (!token) {
    stopLatencyPing(gt);
    ch.shouldReconnect = false;
    ch.authed = false;
    setStatus(gt, "DISCONNECTED");
    ch.handlers.forEach((h) => h.onAuthFail?.("NO_TOKEN"));
    return;
  }

  ch.attempt += 1;
  const attempt = ch.attempt;
  ch.shouldReconnect = true;
  ch.authed = false;

  if (ch.uiStatusTimer) window.clearTimeout(ch.uiStatusTimer);
  if (attempt === 1) setStatus(gt, "CONNECTING");
  else {
    ch.uiStatusTimer = window.setTimeout(() => {
      if (ch.ws && ch.ws.readyState === WebSocket.OPEN) return;
      setStatus(gt, "RECONNECTING");
    }, 700);
  }

  const ws = new WebSocket(getBeWsUrl(gt));
  ch.ws = ws;

  ws.onopen = () => {
    try {
      ws.send(JSON.stringify({ t: "AUTH", token }));
    } catch {}
  };

  ws.onmessage = (ev) => {
    try {
      const msg = JSON.parse(ev.data as any);
      if (msg?.t === "CLIENT_PONG") {
        handleClientPongMsg(gt, msg);
        return;
      }
      if (msg?.t === "AUTH_ACK") {
        if (msg.ok) {
          ch.authed = true;
          setStatus(gt, "ONLINE");
          startLatencyPing(gt);
          ch.handlers.forEach((h) => h.onAuthOk?.());
        } else {
          stopLatencyPing(gt);
          ch.shouldReconnect = false;
          setStatus(gt, "DISCONNECTED");
          ch.handlers.forEach((h) => h.onAuthFail?.(msg.error || "UNAUTHORIZED"));
          try {
            ws.close();
          } catch {}
        }
      }
      ch.handlers.forEach((h) => h.onMessage(msg));
    } catch {
      // ignore
    }
  };

  ws.onerror = () => {
    // close will trigger reconnect
  };

  ws.onclose = () => {
    stopLatencyPing(gt);
    ch.ws = null;
    ch.authed = false;
    if (!ch.shouldReconnect) {
      setStatus(gt, "DISCONNECTED");
      return;
    }
    const jitter = Math.floor(Math.random() * 140);
    const base = 250;
    const step = 220;
    const backoff = Math.min(3_000, base + attempt * step) + jitter;
    if (ch.retryTimer) window.clearTimeout(ch.retryTimer);
    ch.retryTimer = window.setTimeout(() => ensureConnect(gt), backoff);
  };
}

/** WS RTT(ms) 구독. `gameType` 생략 시 두 채널 중 측정된 RTT의 최대값 */
export function wsSubscribeRtt(fn: (ms: number | null) => void, gameType?: WsGameType) {
  if (gameType) {
    const ch = channels[gameType];
    ch.rttListeners.add(fn);
    fn(ch.lastRttMs);
    return () => {
      ch.rttListeners.delete(fn);
    };
  }
  mergedRttListeners.add(fn);
  const a = channels.SEOTDA.lastRttMs;
  const b = channels.HOLDEM.lastRttMs;
  const nums = [a, b].filter((x): x is number => x != null && Number.isFinite(x));
  fn(nums.length ? Math.max(...nums) : null);
  return () => {
    mergedRttListeners.delete(fn);
  };
}

export function wsSubscribe(handlers: WsHandlers, gameType: WsGameType) {
  const ch = channels[gameType];
  ch.handlers.add(handlers);
  ensureConnect(gameType);

  if (ch.status === "ONLINE" && ch.authed) {
    window.setTimeout(() => {
      startLatencyPing(gameType);
      handlers.onAuthOk?.();
    }, 0);
  }

  return () => {
    ch.handlers.delete(handlers);
    if (ch.handlers.size === 0 && shouldCloseSocketWhenNoHandlers()) {
      shutdownChannel(gameType);
    }
  };
}

export function wsSubscribeStatus(fn: StatusListener, gameType: WsGameType) {
  const ch = channels[gameType];
  ch.statusListeners.add(fn);
  fn(ch.status);
  return () => {
    ch.statusListeners.delete(fn);
  };
}

export function wsSend(msg: unknown, gameType: WsGameType) {
  const ch = channels[gameType];
  const ws = ch.ws;
  if (!ws || ws.readyState !== WebSocket.OPEN) return false;
  if (!ch.authed) return false;
  try {
    ws.send(JSON.stringify(msg));
    return true;
  } catch {
    return false;
  }
}

export function wsSendDebugReason(gameType: WsGameType): string {
  const ch = channels[gameType];
  const ws = ch.ws;
  if (!ws || ws.readyState !== WebSocket.OPEN) return "WS_NOT_OPEN";
  if (!ch.authed) return "WS_NOT_AUTHED";
  return "OK";
}
